import { getCurrentLang, handleHtmlTags, toISO8601WithOffset } from "@platformx/utilities";
import { buildContentUrl, getWebsiteId, omitEmpty } from "./common";
import { getPublisherNode } from "./publisher";

export type PollStructureDataInput = {
  title?: string;
  description?: string;
  /** Slug (preferred) or page id used in the delivery route. */
  pageUrl?: string;
  selectedBrandRoute?: string;
  /** Answer texts, in author order. */
  options?: any[];
  startTime?: string;
  /** Scheduled unpublish, when one is set — the poll's closing time. */
  endTime?: string;
  language?: string;
};

/**
 * `WebPage` markup carrying the vote under `potentialAction` (KIWI-6284).
 *
 * The previous output was a bare top-level `VoteAction`, which Google cannot
 * parse as a page: the page node has to be the root, with the action hanging
 * off it, and the answers under `actionOption` rather than `option`.
 */
export const buildPollStructureData = ({
  title = "",
  description = "",
  pageUrl = "",
  selectedBrandRoute = "en",
  options = [],
  startTime = "",
  endTime = "",
  language = "",
}: PollStructureDataInput = {}) => {
  const url = buildContentUrl("poll", pageUrl, selectedBrandRoute);
  const websiteId = getWebsiteId();
  const name = handleHtmlTags(title);
  const actionOption = (options || []).map((option) => handleHtmlTags(option)).filter(Boolean);

  // A vote with no question and no answers is not an action anyone can take —
  // emit the page node alone rather than a hollow VoteAction.
  const hasVote = Boolean(name) || actionOption.length > 0;
  const potentialAction = hasVote
    ? omitEmpty({
        "@type": "VoteAction",
        name,
        actionStatus: "https://schema.org/PotentialActionStatus",
        startTime: toISO8601WithOffset(startTime || new Date().toISOString()),
        endTime: toISO8601WithOffset(endTime),
        actionOption,
        target: url ? { "@type": "EntryPoint", urlTemplate: url } : "",
      })
    : undefined;

  return omitEmpty({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url ? `${url}#webpage` : "",
    url,
    name,
    description: handleHtmlTags(description),
    inLanguage: language || getCurrentLang(),
    isPartOf: websiteId ? { "@id": websiteId } : "",
    publisher: getPublisherNode(),
    potentialAction,
  });
};
